import { useQuery } from '@tanstack/react-query';
import { competencyLabel } from '@shared/competencies';
import { api } from '../api/client.js';
import { levelLabel } from '../pages/PersonCard.js';
import type { EligibilityResult, Position } from '@shared/types';

/**
 * แผนผู้ทดแทนและการถ่ายทอดงานของอัตราหนึ่ง — เปิดจากปุ่ม "ดูแผน" ในตารางอัตราที่จะว่าง
 *
 * ทำตามแบบในแคนวาส artboard "Plan" เท่าที่ข้อมูลในระบบมี
 *
 * ════════════════════════════════════════════════════════════════
 *  ⚠ อ่านก่อนแก้ — หน้านี้ "อ่านอย่างเดียว" และไม่ใช่เพราะยังเขียนไม่เสร็จ
 * ════════════════════════════════════════════════════════════════
 *
 * แบบที่ออกแบบไว้มีของสี่อย่างที่ต้อง "กดแล้วบันทึก"
 *
 *   ยืนยันผู้ทดแทน          ต้องมีตารางผู้ทดแทนต่ออัตรา + ใครยืนยัน + เมื่อไร + ถอนเมื่อไร
 *   ผู้รับผิดชอบแผน          ต้องมีตารางแผนต่ออัตรา
 *   ความสำคัญของงาน        ต้องมีการประเมินของ HR ต่ออัตรา
 *   สถานะงานถ่ายทอด        ต้องมีรายการงานที่แก้ไขได้ + บันทึกการเปลี่ยนแปลง
 *
 * ทั้งสี่อย่างเป็น "ข้อมูลที่ผู้ใช้แก้ได้" ซึ่งเป็นความสามารถที่ระบบนี้ยังไม่มีเลยสักที่ —
 * วันนี้ทุกหน้าอ่านอย่างเดียวจาก dump ของ DPIS
 *
 * ⚠ ห้ามใส่ปุ่มยืนยันที่กดแล้วเก็บไว้ใน state ของหน้าจอ ปุ่มแบบนั้นพังร้ายกว่าไม่มีปุ่ม
 *   เพราะผู้ใช้กดแล้วเชื่อว่าบันทึกแล้ว พอรีเฟรชหน้าค่าหาย โดยไม่มีอะไรบอกว่าหาย
 *   และการแต่งตั้งเป็นเรื่องที่คนจำได้ว่า "ทำไปแล้ว"
 *
 * การ์ด "สิ่งที่ยังบันทึกในระบบไม่ได้" ทางขวาจึงมีไว้บอกตรง ๆ ว่าขาดอะไร
 * ไม่ใช่เว้นช่องว่างไว้เฉย ๆ ให้ผู้ใช้เดาเอง
 *
 * ────────────────────────────────────────────────────────────────
 *  ⚠ รายชื่อผู้ทดแทนมาจากกฎ ไม่ได้มาจากโมเดลภาษา
 * ────────────────────────────────────────────────────────────────
 *
 * ในแบบเขียนว่า "ผู้ช่วย AI เสนอ" แต่ของจริงมาจากเครื่องตรวจเกณฑ์ระยะเวลาการดำรง
 * ตำแหน่งตามมาตรฐานกำหนดตำแหน่ง (services/vacancyCandidates) ซึ่งเป็นกฎล้วน ๆ
 * ตรวจย้อนได้ทุกข้อ
 *
 * จึงเขียนบนหน้าจอว่า "ระบบคัดจากเกณฑ์" ไม่ใช่ "AI เสนอ" — ติดป้ายว่าเป็น AI
 * ทั้งที่เป็นกฎ ทำให้ผู้ใช้ไม่เชื่อผลที่ตรวจย้อนได้จริง และทำให้คนที่อยากรู้ว่า
 * "ทำไมถึงได้ชื่อนี้" ไม่รู้ว่าต้องไปเปิดเอกสารฉบับไหน
 *
 * ────────────────────────────────────────────────────────────────
 *  ⚠ ลำดับในรายชื่อไม่ใช่ลำดับความเหมาะสม
 * ────────────────────────────────────────────────────────────────
 *
 * เรียงตาม seniorityRank = ลำดับอาวุโสในกลุ่มผู้เข้าเงื่อนไขของอัตรานี้
 * ไม่ใช่คะแนนความเหมาะสม ระบบไม่ให้คะแนนความเหมาะสมและไม่จัดอันดับด้วยคะแนน
 * (ดูคำเตือนที่ latestPerformance ใน domain/types.ts)
 *
 * จึงไม่มีเลขลำดับข้างชื่อ และมีคำกำกับใต้รายชื่อเสมอว่าเรียงด้วยอะไร
 */

/** แสดงมากสุดกี่คน — ตรงกับที่ผู้ใช้สั่งไว้ตอนออกแบบ */
const TOP = 5;

export function SuccessionPlanView({
  pos,
  onOpenProfile,
}: {
  pos: Position;
  onOpenProfile: (personaId: string, name: string) => void;
}) {
  const cand = useQuery({
    queryKey: ['vacancy-candidates', pos.posId, 'criteria'],
    queryFn: () => api.vacancyCandidates(pos.posId),
  });

  const qual = useQuery({
    queryKey: ['position-qualification', pos.posId],
    queryFn: () => api.positionQualification(pos.posId),
  });

  const eligible = cand.data?.eligible ?? null;
  const passed = cand.data?.eligibleTotals?.passed ?? null;
  const top = (eligible ?? []).slice(0, TOP);

  const inc = pos.incumbent;

  /**
   * ⚠ แสดงหน้าที่เฉพาะแถวที่ "ไม่ได้ติดป้ายสังเคราะห์" เท่านั้น
   *
   * ตอนตรวจกับข้อมูลจริงพบว่า 618 จาก 624 อัตราที่จะเกษียณ ติดป้าย fileRequirementSynthetic
   * และข้อความหน้าที่ทั้งชุดมีแค่สองแบบ ซึ่งเป็นแม่แบบล้วน ๆ
   *
   *   "ปฏิบัติงานตามหน้าที่และความรับผิดชอบของตำแหน่ง ⟨ชื่อตำแหน่ง⟩"
   *   "สนับสนุนภารกิจและเป้าหมายของหน่วยงาน"
   *
   * ประโยคเดียวกันทุกตำแหน่งทั้งกระทรวง เอามาวางใต้หัวข้อ "เรื่องที่ต้องถ่ายทอดให้ผู้ทดแทน"
   * จะอ่านเหมือนระบบประมวลมาให้จริง ทั้งที่ไม่ได้บอกอะไรเลยสักคำ ติดป้ายกำกับก็ไม่พอ
   * เพราะคนที่พิมพ์หน้านี้ออกไปแนบเรื่องจะเหลือแค่ข้อความ ไม่เหลือป้าย
   *
   * ⚠ ตัดสินจากธงที่เซิร์ฟเวอร์ส่งมา ห้ามเขียนเงื่อนไขเทียบข้อความตายไว้ที่นี่
   *   วันที่ได้หน้าที่จริงจากมาตรฐานกำหนดตำแหน่งของ ก.พ. ธงจะเป็น false เอง
   *   แล้วแถวจะโผล่มาเองโดยไม่ต้องแก้ไฟล์นี้
   */
  const synthetic = qual.data?.fileRequirementSynthetic !== false;
  const duties = synthetic ? [] : (qual.data?.fileRequirement?.responsibilities ?? []);
  const comps = synthetic ? [] : (qual.data?.fileRequirement?.requiredCompetencies ?? []);

  return (
    <div className="spv">
      {/* ────────── แถบข้อเท็จจริง ──────────
          สี่ช่องเหมือนแบบ แต่สองช่องท้ายเปลี่ยนเป็นค่าที่ระบบมีจริง
          แบบใช้ "ความสำคัญของงาน" (ต้องมีการประเมินของ HR) กับ "ผู้รับผิดชอบแผน"
          (ต้องมีตารางแผน) ซึ่งยังไม่มีทั้งคู่ */}
      <div className="spv-facts">
        <div>
          <span className="k">อัตราจะว่างลง</span>
          <b className="tnum">{thaiFull(inc?.retireDate ?? null)}</b>
          <span className="s tnum">{untilText(inc?.retireDate ?? null)}</span>
        </div>
        <div>
          <span className="k">ผู้ครองปัจจุบัน</span>
          <b>{inc?.name ?? '—'}</b>
          <span className="s tnum">
            {inc?.occupyDate ? `ครองอัตรานี้มาแล้ว ${sinceText(inc.occupyDate)}` : 'ไม่มีวันที่เข้าครองในทะเบียน'}
          </span>
        </div>
        <div>
          <span className="k">ผู้เข้าเงื่อนไข</span>
          <b className="tnum">{passed === null ? '—' : `${passed.toLocaleString('th-TH')} คน`}</b>
          <span className="s">ผ่านเกณฑ์ระยะเวลาการดำรงตำแหน่ง</span>
        </div>
        <div>
          <span className="k">เกณฑ์ที่ใช้ตรวจ</span>
          {/* ⚠ "ไม่มีเอกสาร" กับ "ไม่มีใครผ่าน" คนละเรื่องกัน ต้องเขียนแยกให้ชัด */}
          <b className="is-sm">{cand.data?.standardTitle ?? 'ยังไม่มีมาตรฐานของระดับนี้ในระบบ'}</b>
          <span className="s">มาตรฐานกำหนดตำแหน่ง ก.พ.</span>
        </div>
      </div>

      <div className="spv-grid">
        <div className="spv-main">
          {/* ────────── ผู้ทดแทน ────────── */}
          <section className="spv-sec">
            <h3>ผู้ทดแทน</h3>

            {/* ⚠ "ยังไม่มีผู้ทดแทนที่ยืนยันแล้ว" ต้องแยกออกจาก "ไม่มีผู้เหมาะสม" ให้ชัด
                สองประโยคนี้ต่างกันคนละขั้ว และคนที่อ่านผ่าน ๆ จะอ่านเป็นอย่างหลังเสมอ */}
            <div className="spv-empty">
              <b>ยังไม่มีผู้ทดแทนที่ยืนยันแล้ว</b>
              <p>
                ไม่ได้แปลว่าไม่มีผู้เหมาะสม — ระบบนี้ยังไม่มีที่ให้บันทึกการยืนยันผู้ทดแทน
                การยืนยันเป็นการตัดสินใจของผู้รับผิดชอบ ไม่ใช่สิ่งที่ระบบตัดสินให้เอง
              </p>
            </div>

            {cand.isPending && <p className="state sm">กำลังคัดรายชื่อผู้เข้าเงื่อนไข…</p>}
            {cand.isError && <p className="state sm error">{String(cand.error)}</p>}

            {cand.data && eligible === null && (
              <p className="state sm">
                ระบบยังไม่มีมาตรฐานกำหนดตำแหน่งของระดับ{pos.level} สายงาน{pos.line} จึงคัดผู้เข้าเงื่อนไขให้ไม่ได้
              </p>
            )}

            {cand.data && eligible !== null && top.length === 0 && (
              <p className="state sm">ไม่มีผู้ผ่านเกณฑ์ระยะเวลาสำหรับอัตรานี้</p>
            )}

            {top.length > 0 && (
              <>
                <p className="spv-from">
                  ระบบคัดมา {top.length} คน
                  {passed !== null && passed > top.length && (
                    <> — จากผู้เข้าเงื่อนไข {passed.toLocaleString('th-TH')} คน แสดงมากสุด {TOP} คน</>
                  )}
                </p>

                <ul className="spv-cands">
                  {top.map((c) => (
                    <CandidateRow
                      key={c.personaId ?? c.name}
                      c={c}
                      posRetire={inc?.retireDate ?? null}
                      onOpenProfile={onOpenProfile}
                    />
                  ))}
                </ul>

                {/* ⚠ คำกำกับนี้ห้ามตัดออก — ไม่มีมัน รายชื่อเรียงกันห้าบรรทัด
                    จะถูกอ่านเป็นการจัดอันดับความเหมาะสมเสมอ ซึ่งระบบไม่ได้ทำ */}
                <p className="spv-note">
                  เรียงตามลำดับอาวุโสในกลุ่มผู้เข้าเงื่อนไขของอัตรานี้ ไม่ใช่การจัดอันดับความเหมาะสม ·
                  ข้อเสนอมาจากเกณฑ์ที่ตรวจได้เท่านั้น คือระยะเวลาการดำรงตำแหน่งตามมาตรฐานกำหนดตำแหน่ง
                  ไม่ได้ประเมินผลงานหรือความเหมาะสมกับงานของอัตรานี้
                </p>
              </>
            )}
          </section>

          {/* ────────── เรื่องที่ต้องถ่ายทอด ────────── */}
          <section className="spv-sec">
            <h3>เรื่องที่ต้องถ่ายทอดให้ผู้ทดแทน</h3>
            <p className="spv-sub">
              ประมวลจากคุณสมบัติของอัตรานี้ ไม่ได้พิมพ์เพิ่มเอง — ทุกแถวบอกได้ว่ามาจากไหน
            </p>

            <div className="spv-tbl">
              <table>
                <thead>
                  <tr>
                    <th>เรื่อง</th>
                    <th>ที่มา</th>
                  </tr>
                </thead>
                <tbody>
                  {/* กลุ่ม 1 — หน้าที่ของตำแหน่งนี้ */}
                  <tr className="spv-grp">
                    <th colSpan={2} scope="colgroup">หน้าที่ความรับผิดชอบของตำแหน่งนี้</th>
                  </tr>
                  {qual.isPending && (
                    <tr><td colSpan={2} className="spv-muted">กำลังอ่านเกณฑ์ของตำแหน่ง…</td></tr>
                  )}
                  {qual.data && duties.length === 0 && (
                    <tr>
                      <td colSpan={2} className="spv-muted">
                        ระบบยังไม่มีหน้าที่ของตำแหน่งนี้ที่ใช้อ้างอิงได้ — ตารางเกณฑ์ตำแหน่งที่มีอยู่
                        เป็นข้อความแม่แบบชุดเดียวกันทุกตำแหน่ง จึงบอกไม่ได้ว่าอัตรานี้ทำอะไรบ้าง
                        ต้องได้หน้าที่จากมาตรฐานกำหนดตำแหน่งของ ก.พ. ฉบับที่ตรงกับสายงานและระดับก่อน
                      </td>
                    </tr>
                  )}
                  {duties.map((d) => (
                    <tr key={d}>
                      <td>{d}</td>
                      <td className="spv-srccol">เกณฑ์ตำแหน่งตามชื่อตำแหน่ง</td>
                    </tr>
                  ))}

                  {/* กลุ่ม 2 — สิ่งที่ผูกกับอัตรานี้เป็นราย ๆ */}
                  <tr className="spv-grp">
                    <th colSpan={2} scope="colgroup">สิ่งที่ผูกอยู่กับอัตรานี้โดยเฉพาะ</th>
                  </tr>
                  <tr>
                    <td colSpan={2} className="spv-muted">
                      สิทธิ์การใช้งานระบบ · คำสั่งแต่งตั้งคณะกรรมการ · ทะเบียนผู้ประสานงาน —
                      สามอย่างนี้ผูกกับอัตราเป็นราย ๆ และอยู่นอก DPIS ระบบจึงประมวลให้ไม่ได้
                      ต้องรวบรวมจากต้นทางของแต่ละทะเบียนก่อน
                    </td>
                  </tr>

                  {/* กลุ่ม 3 — ความรู้ที่ไม่มีเอกสาร */}
                  <tr className="spv-grp">
                    <th colSpan={2} scope="colgroup">ความรู้ที่ไม่ได้อยู่ในเอกสารใด</th>
                  </tr>
                  <tr className="spv-gold">
                    <td colSpan={2}>
                      วิธีทำงานจริง ข้อตกลงที่ไม่ได้เขียนไว้ และคนที่ต้องรู้จัก —
                      ระบบไม่มีข้อมูลส่วนนี้และประมวลให้ไม่ได้ ต้องนัดคุยกับผู้ครองปัจจุบันโดยตรง
                      ก่อนวันที่อัตราว่างลง
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {comps.length > 0 && (
              <p className="spv-note">
                สมรรถนะที่เกณฑ์ของตำแหน่งนี้ระบุไว้: {comps.map(competencyLabel).join(' · ')}
              </p>
            )}
          </section>
        </div>

        {/* ────────── สิ่งที่ยังบันทึกไม่ได้ ──────────
            ช่องนี้ในแบบคือ "บันทึกการเปลี่ยนแปลงแผน" กับ "ทำให้แผนนี้สมบูรณ์"
            ทั้งสองอย่างต้องมีข้อมูลที่แก้ไขได้ จึงยังทำไม่ได้

            ⚠ บอกตรง ๆ ว่าขาดอะไร ดีกว่าเว้นช่องว่างไว้ให้เดา — ผู้ใช้ที่เห็นหน้าครึ่งเดียว
              โดยไม่มีคำอธิบาย จะสรุปว่าระบบพัง ไม่ใช่ว่าข้อมูลยังไม่มี */}
        <aside className="spv-side">
          <section className="spv-gap">
            <h4>สิ่งที่ยังบันทึกในระบบไม่ได้</h4>
            <ul>
              <li>
                <b>ยืนยันผู้ทดแทน</b>
                <span>ต้องมีที่เก็บว่าใครถูกยืนยัน ใครเป็นคนยืนยัน เมื่อไร และถอนเมื่อไร</span>
              </li>
              <li>
                <b>ผู้รับผิดชอบแผน</b>
                <span>ต้องมีแผนต่ออัตรา พร้อมสถานะและวันที่เปิดแผน</span>
              </li>
              <li>
                <b>ความสำคัญของงาน</b>
                <span>ต้องมีการประเมินของ HR ต่ออัตรา ไม่ได้อยู่ในทะเบียนตำแหน่ง</span>
              </li>
              <li>
                <b>สถานะงานถ่ายทอดและบันทึกการเปลี่ยนแปลง</b>
                <span>ต้องมีรายการงานที่แก้ไขได้ และบันทึกว่าใครเปลี่ยนอะไรเมื่อไร</span>
              </li>
            </ul>
            <p>
              ทั้งสี่อย่างเป็นข้อมูลที่ผู้ใช้แก้ได้ ซึ่งเป็นความสามารถที่ระบบยังไม่มีเลยสักที่ —
              วันนี้ทุกหน้าอ่านอย่างเดียวจากข้อมูลที่ DPIS ส่งมา
            </p>
          </section>
        </aside>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function CandidateRow({
  c,
  posRetire,
  onOpenProfile,
}: {
  c: EligibilityResult;
  /** วันที่อัตรานี้จะว่างลง — ใช้เทียบว่าผู้เข้าเงื่อนไขคนนี้เกษียณก่อนหรือหลัง */
  posRetire: string | null;
  onOpenProfile: (personaId: string, name: string) => void;
}) {
  const manual = c.pendingManualChecks.length;

  /**
   * เกษียณก่อนอัตรานี้กี่ปี — ข้อจำกัดที่ต้องขึ้นคู่กับชื่อ ไม่ใช่ให้ไปเจอเอาตอนเสนอเรื่อง
   *
   * ⚠ ขึ้นเฉพาะกรณี "ก่อน" เท่านั้น คนที่เกษียณหลังอัตรานี้ไม่ใช่ข้อจำกัด
   *   ป้ายเตือนที่ขึ้นกับทุกคนคือป้ายที่ไม่มีใครอ่าน
   *
   * ⚠ retiresWithinMonths เป็น null เมื่อยังอีกไกล จึงต้องเช็ก null ไม่ใช่เทียบ 0
   *   (ค่า 0 มีจริงในข้อมูล แปลว่าเกษียณปีงบนี้ ไม่ใช่แปลว่าไม่มีข้อมูล)
   */
  const posMonths = posRetire ? monthsBetween(new Date(), new Date(posRetire)) : null;
  const early =
    c.retiresWithinMonths !== null && posMonths !== null && c.retiresWithinMonths < posMonths
      ? posMonths - c.retiresWithinMonths
      : null;
  return (
    <li className="spv-cand">
      <div className="spv-who">
        <b>{c.name}</b>
        <span className="tnum">
          {c.currentTitle} {levelLabel(c.currentLevel)} · {c.unit}
        </span>

        <div className="spv-chips">
          {/* ⚠ เขียวในระบบนี้แปลว่า "ตรงกับเกณฑ์ที่คนตั้งไว้" ไม่ใช่ "ดี" */}
          {/* ⚠ ยุบ "ผ่านเกณฑ์" กับ "ผ่านข้อ 1.2, 1.3" เป็นป้ายเดียว
              ตอนดูข้อมูลจริงพบว่าผู้เข้าเงื่อนไขทุกคนของอัตราเดียวกันมักผ่านข้อเดียวกันหมด
              สองป้ายจึงพูดเรื่องเดียวกันซ้ำสองครั้งในทุกแถว แต่เลขข้อต้องไม่หาย
              เพราะเป็นตัวที่ทำให้ตามไปเปิดเอกสารได้ว่าผ่านด้วยเงื่อนไขข้อไหน */}
          {c.meetsTimeRequirement && (
            <span className="spv-ok tnum">
              ผ่านเกณฑ์ระยะเวลา
              {c.passedPaths.length > 0 && ` (ข้อ ${c.passedPaths.join(', ')})`}
            </span>
          )}
          {c.currentLevelSince && (
            <span className="tnum">อยู่ระดับปัจจุบันมา {sinceText(c.currentLevelSince)}</span>
          )}
          {/* ข้อจำกัดขึ้นคู่กับชื่อ ไม่ใช่ให้ไปเจอเอาตอนเสนอเรื่อง */}
          {manual > 0 && <span className="spv-warn tnum">ต้องให้เจ้าหน้าที่ตรวจเองอีก {manual} ข้อ</span>}
          {early !== null && (
            <span className="spv-warn tnum">เกษียณก่อนอัตรานี้ {monthsTH(early)}</span>
          )}
        </div>
      </div>

      {/* ⚠ ไม่มีปุ่ม "ยืนยันเป็นผู้ทดแทน" ตามแบบ เพราะยังไม่มีที่ให้บันทึก
          ปุ่มที่กดแล้วไม่เกิดอะไร หรือเกิดแล้วหายตอนรีเฟรช พังร้ายกว่าไม่มีปุ่ม */}
      {c.personaId && (
        <button type="button" onClick={() => onOpenProfile(c.personaId!, c.name)}>
          ดูประวัติ
        </button>
      )}
    </li>
  );
}

/* ---------- วันที่ ---------- */

const TH_MONTH = [
  'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
  'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.',
];

function thaiFull(iso: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return '—';
  return `${Number(d)} ${TH_MONTH[Number(m) - 1] ?? ''} ${Number(y) + 543}`;
}

function monthsBetween(from: Date, to: Date): number {
  return (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
}

function monthsTH(months: number): string {
  if (months <= 0) return 'ไม่ถึง 1 เดือน';
  const y = Math.floor(months / 12);
  const m = months % 12;
  return [y > 0 ? `${y} ปี` : '', m > 0 ? `${m} เดือน` : ''].filter(Boolean).join(' ') || 'ไม่ถึง 1 เดือน';
}

function sinceText(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return monthsTH(monthsBetween(d, new Date()));
}

function untilText(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const m = monthsBetween(new Date(), d);
  return m <= 0 ? 'ว่างแล้ว' : `อีก ${monthsTH(m)}`;
}
